/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type * as RadixUIScrollArea from "@radix-ui/react-scroll-area";
import type { ComponentProps, ReactNode } from "react";
import { useContext, useEffect } from "react";

import { numericPercentages, spacing } from "../../../../../global/emotion";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import { ScrollArea } from "../../../blocks/other/ScrollArea/ScrollArea.emotion";
import { ModalTabContext } from "../ModalTabContainer/ModalTabContainer.emotion";

const baseTransitionStyle: Interpolation = {
  display: "grid",
  gridTemplateAreas: '"header" "body" "footer"',
  gridTemplateColumns: "1fr",
  gridTemplateRows: "min-content minmax(calc(100% - 96px), 1fr) min-content",
  position: "absolute",
  transform: "translateX(200%)",
  transition: "transform 300ms ease-in",
};
const enteringTransitionStyle: Interpolation = {
  transform: "translateX(200%)",
};
const enteredTransitionStyle: Interpolation = {
  position: "relative",
  transform: "translateX(0)",
};
const exitingTransitionStyle: Interpolation = {
  transform: "translateX(-200%)",
};
const exitedTransitionStyle: Interpolation = {
  transform: "translateX(-200%)",
};
const establishedTransitionStyle: Interpolation = {
  height: numericPercentages[100],
};

const footerStyle: Interpolation = {
  gridArea: "footer",
};
const headerStyle: Interpolation = {
  gridArea: "header",
};
const areaContentStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  rowGap: spacing.xxs,
};
const scrollRootStyle: Interpolation = {
  gridArea: "body",
};

export type ModalTabContentProps = HallowElementProps<"div"> & {
  areaContentCSS?: Interpolation;
  value: string;
  header?: ReactNode;
  footer?: ReactNode;
  maskBottom?: boolean;
  onVisible?: () => void;
  viewportProps?: ComponentProps<typeof RadixUIScrollArea.Viewport>;
};

export const ModalTabContent = ({
  areaContentCSS = {},
  children,
  maskBottom = true,
  value,
  footer,
  header,
  viewportProps,
  onVisible,
  ...props
}: ModalTabContentProps) => {
  const tabs = useContext(ModalTabContext);

  useEffect(() => {
    if (tabs.entered === value) {
      onVisible?.();
    }
  }, [tabs.entered]);

  if (
    [tabs.entering, tabs.exited, tabs.entered, tabs.exiting].includes(value)
  ) {
    const style =
      tabs.entering === value
        ? enteringTransitionStyle
        : tabs.entered === value
          ? enteredTransitionStyle
          : tabs.exiting === value
            ? exitingTransitionStyle
            : exitedTransitionStyle;

    return (
      <div
        ref={tabs.setHeight.bind(null, value)}
        css={[
          baseTransitionStyle,
          style,
          tabs.established === value ? establishedTransitionStyle : null,
        ]}
        {...props}
      >
        {header ? <div css={headerStyle}>{header}</div> : null}
        <ScrollArea
          horizontal={false}
          maskBottom={maskBottom}
          masksSize="xs"
          maskTop
          viewportProps={viewportProps}
          masksColor={"onOverlayBackground"}
          css={scrollRootStyle}
        >
          <div css={[areaContentStyle, areaContentCSS]}>{children}</div>
        </ScrollArea>
        {footer ? <div css={footerStyle}>{footer}</div> : null}
      </div>
    );
  }

  return null;
};
